import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Namespace } from '@data/index';
import { useDynamicTrialsFiltersStore } from '@data/storage';
import { AntdTooltip, Button } from '@ui/components';
import { Filter } from './components';

const MAX_LOCATIONS_COUNT = 10;

type TrialsFiltersListProps = {
  clearAllFilters: () => void;
  countries: string[];
  handleCategoryIdsChange: (value: string, label?: string) => void;
  handleCountriesChange: (value: string) => void;
  handleLeadSponsorsChange: (value: string, label?: string) => void;
  handleLocationChange: (value: string, label?: string) => void;
  handleOverallStatusChange: (value: string) => void;
  overallStatuses: string[];
};

export const TrialsFiltersList = ({
  countries,
  overallStatuses,
  clearAllFilters,
  handleLocationChange,
  handleCountriesChange,
  handleCategoryIdsChange,
  handleLeadSponsorsChange,
  handleOverallStatusChange,
}: TrialsFiltersListProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { categories } = useDynamicTrialsFiltersStore();

  const sponsors = categories.filter(({ type }) => type === 'sponsor');
  const icdCodes = categories.filter(({ type }) => type === 'category');
  const locations = categories.filter(({ type }) => type === 'location');

  if (!categories?.length && !countries?.length && !overallStatuses?.length && !locations?.length)
    return null;
  return (
    <div className="px-4 mb-4 flex flex-wrap gap-1">
      {icdCodes.map(({ label, value }) => (
        <Filter
          key={value}
          isDynamic
          type="categories"
          name={label}
          onClose={() => handleCategoryIdsChange(value, label)}
        />
      ))}
      {sponsors.map(({ label, value }) => (
        <Filter
          key={value}
          isDynamic
          type="sponsors"
          name={label}
          onClose={() => handleLeadSponsorsChange(value, label)}
        />
      ))}
      {overallStatuses.map((overallStatus) => (
        <Filter
          key={overallStatus}
          type="overall_status"
          name={overallStatus}
          onClose={() => handleOverallStatusChange(overallStatus)}
        />
      ))}
      {countries.map((country) => (
        <Filter
          key={country}
          isDynamic
          type="countries"
          name={country}
          onClose={() => handleCountriesChange(country)}
        />
      ))}
      {locations.length <= MAX_LOCATIONS_COUNT ? (
        <>
          {locations.map(({ label, value }) => (
            <Filter
              key={value}
              isDynamic
              type="locations"
              name={label}
              onClose={() => handleLocationChange(value, label)}
            />
          ))}
        </>
      ) : (
        <>
          {locations.slice(0, MAX_LOCATIONS_COUNT).map(({ label, value }) => (
            <Filter
              key={value}
              isDynamic
              type="locations"
              name={label}
              onClose={() => handleLocationChange(value, label)}
            />
          ))}

          <AntdTooltip
            title={`${locations
              .slice(MAX_LOCATIONS_COUNT)
              .map(({ label }) => label)
              .join(', ')}`}
            classNames={{ root: 'min-w-[350px] w-full' }}
          >
            <div>
              <Filter
                isDynamic
                type="more_locations"
                name={t('trials_library.filters_preview.more_locations', {
                  count: locations.length - MAX_LOCATIONS_COUNT,
                })}
              />
            </div>
          </AntdTooltip>
        </>
      )}

      <Button
        type="text"
        icon={<CloseOutlined />}
        onClick={clearAllFilters}
        className="text-error h-auto"
      >
        {t('trials_library.clear_filters')}
      </Button>
    </div>
  );
};
